import { useMemo } from 'react';
import { convertUTCToLocal } from 'src/utils/dateTimeUtils';

// Get column type for proper conversion
const getColumnType = (field) => {
  const numberFields = ["kw_consumed", "totalAmountPaid", "totalAmountCharged", "totalRefund"];
  const dateFields = ["starttime", "stoptime"];
  
  if (numberFields.includes(field)) return "number";
  if (dateFields.includes(field)) return "date";
  return "string";
};

// Helper function to normalize dates for comparison
const normalizeDateForComparison = (dateValue, filterValue, operator) => {
  if (!dateValue || !filterValue) {
    return { rowDate: null, filterDate: null };
  }
  
  // Convert UTC timestamp from database to local date
  const localDate = convertUTCToLocal(dateValue);
  if (!localDate) {
    return { rowDate: null, filterDate: null };
  }
  
  // Create filter date from the input (assume it's in local timezone)
  const filterDate = new Date(filterValue);
  if (isNaN(filterDate.getTime())) {
    return { rowDate: null, filterDate: null };
  }
  
  // For inclusive operators (onOrAfter, onOrBefore), we need to handle the full day
  if (operator === 'onOrAfter' || operator === 'onOrBefore' || operator === 'equals') {
    // For "onOrAfter" and "equals", set filter date to start of day (00:00:00)
    if (operator === 'onOrAfter' || operator === 'equals') {
      filterDate.setHours(0, 0, 0, 0);
    }
    // For "onOrBefore", set filter date to end of day (23:59:59.999)
    else if (operator === 'onOrBefore') {
      filterDate.setHours(23, 59, 59, 999);
    }
  }
  
  return { rowDate: localDate, filterDate };
};

// Apply a single filter to a row
const applySingleFilter = (row, filter) => {
  const { field, operator, value } = filter;
  const rawValue = row[field];
  const columnType = getColumnType(field);

  // Handle empty value checks
  if (['isEmpty', 'isNotEmpty'].includes(operator)) {
    const isEmpty = rawValue === null || rawValue === undefined || rawValue === '';
    const result = operator === 'isEmpty' ? isEmpty : !isEmpty;
    return result;
  }

  // Skip filters with empty values
  if (!value && !['isEmpty', 'isNotEmpty'].includes(operator)) {
    return true;
  }

  const stringValue = String(rawValue ?? "").toLowerCase();
  const filterValue = String(value).toLowerCase();

  switch (operator) {
    // String operators
    case 'contains':
      return stringValue.includes(filterValue);
    case 'notContains':
      return !stringValue.includes(filterValue);
    case 'equals':
    case 'is':
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'equals');
        if (!rowDate || !filterDate) return false;
        // Compare local dates by creating new Date objects with just the date part
        const rowLocalDate = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());
        const filterLocalDate = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
        const result = rowLocalDate.getTime() === filterLocalDate.getTime();
        return result;
      }
      return stringValue === filterValue;
    case 'notEquals':
    case 'notEqual':
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'equals');
        if (!rowDate || !filterDate) return true;
        // Compare local dates by creating new Date objects with just the date part
        const rowLocalDate = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());
        const filterLocalDate = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
        const result = rowLocalDate.getTime() !== filterLocalDate.getTime();
        return result;
      }
      return stringValue !== filterValue;
    case 'startsWith':
      return stringValue.startsWith(filterValue);
    case 'endsWith':
      return stringValue.endsWith(filterValue);
    case 'isAnyOf':
      if (Array.isArray(value)) {
        return value.some(v => stringValue === String(v).toLowerCase());
      }
      return false;

    // Numeric operators
    case 'greaterThan':
    case '>':
      if (columnType === 'number') {
        return Number(rawValue || 0) > Number(value || 0);
      }
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'after');
        if (!rowDate || !filterDate) return false;
        return rowDate > filterDate;
      }
      return stringValue > filterValue;
    case 'greaterThanOrEqual':
    case '>=':
      if (columnType === 'number') {
        return Number(rawValue || 0) >= Number(value || 0);
      }
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'onOrAfter');
        if (!rowDate || !filterDate) return false;
        return rowDate >= filterDate;
      }
      return stringValue >= filterValue;
    case 'lessThan':
    case '<':
      if (columnType === 'number') {
        return Number(rawValue || 0) < Number(value || 0);
      }
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'before');
        if (!rowDate || !filterDate) return false;
        return rowDate < filterDate;
      }
      return stringValue < filterValue;
    case 'lessThanOrEqual':
    case '<=':
      if (columnType === 'number') {
        return Number(rawValue || 0) <= Number(value || 0);
      }
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'onOrBefore');
        if (!rowDate || !filterDate) return false;
        return rowDate <= filterDate;
      }
      return stringValue <= filterValue;

    // Date operators
    case 'after':
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'after');
        if (!rowDate || !filterDate) return false;
        return rowDate > filterDate;
      }
      return stringValue > filterValue;
    case 'onOrAfter':
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'onOrAfter');
        if (!rowDate || !filterDate) {
          return false;
        }
        // Compare local dates by creating new Date objects with just the date part
        const rowLocalDate = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());
        const filterLocalDate = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
        const result = rowLocalDate >= filterLocalDate;
        return result;
      }
      return stringValue >= filterValue;
    case 'before':
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'before');
        if (!rowDate || !filterDate) return false;
        return rowDate < filterDate;
      }
      return stringValue < filterValue;
    case 'onOrBefore':
      if (columnType === 'date') {
        const { rowDate, filterDate } = normalizeDateForComparison(rawValue, value, 'onOrBefore');
        if (!rowDate || !filterDate) {
          return false;
        }
        // Compare local dates by creating new Date objects with just the date part
        const rowLocalDate = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());
        const filterLocalDate = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
        const result = rowLocalDate <= filterLocalDate;
        return result;
      }
      return stringValue <= filterValue;

    // Range operators
    case 'between':
      if (Array.isArray(value) && value.length === 2) {
        const [minValue, maxValue] = value;
        if (columnType === 'number') {
          const numValue = Number(rawValue || 0);
          return numValue >= Number(minValue || 0) && numValue <= Number(maxValue || 0);
        }
        if (columnType === 'date') {
          const { rowDate: minRowDate, filterDate: minFilterDate } = normalizeDateForComparison(rawValue, minValue, 'onOrAfter');
          const { rowDate: maxRowDate, filterDate: maxFilterDate } = normalizeDateForComparison(rawValue, maxValue, 'onOrBefore');
          if (!minRowDate || !minFilterDate || !maxRowDate || !maxFilterDate) return false;
          return minRowDate >= minFilterDate && maxRowDate <= maxFilterDate;
        }
        // String range
        return stringValue >= String(minValue || "").toLowerCase() && 
               stringValue <= String(maxValue || "").toLowerCase();
      }
      return false;

    default:
      return true;
  }
};

// Apply multiple filters with logical operator
const applyFilters = (data, filters, logicalOperator = 'AND') => {
  if (!filters || filters.length === 0) {
    return data;
  }

  const filteredData = data.filter(row => {
    if (logicalOperator === 'OR') {
      const result = filters.some(filter => applySingleFilter(row, filter));
      return result;
    } else {
      const results = filters.map(filter => {
        const result = applySingleFilter(row, filter);
        return result;
      });
      const passed = results.every(result => result);
      return passed;
    }
  });

  return filteredData;
};

// Custom hook for filtering
export const useCustomFilter = (data, filters = [], logicalOperator = 'AND') => {
  const filteredData = useMemo(() => {
    return applyFilters(data, filters, logicalOperator);
  }, [data, filters, logicalOperator]);

  const filterStats = useMemo(() => {
    const totalCount = data.length;
    const filteredCount = filteredData.length;
    const activeFiltersCount = filters.filter(filter => 
      filter.field && filter.operator && 
      (filter.value || ['isEmpty', 'isNotEmpty'].includes(filter.operator))
    ).length;

    return {
      totalCount,
      filteredCount,
      activeFiltersCount,
      isFiltered: activeFiltersCount > 0
    };
  }, [data, filteredData, filters]);

  return {
    filteredData,
    ...filterStats
  };
};
